import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  CreditCard,
  Calendar,
  ExternalLink,
  Loader2,
  Receipt,
  Sparkles,
  AlertCircle,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  AlertMessage,
  Toast,
  ToastType,
} from '../../components/ui'
import { Button } from '../../components/ui'
import { Badge } from '../../components/ui/Badge'
import { PlanCard } from '../../components/plans/PlanCard'
import { BillingToggle } from '../../components/plans/BillingToggle'
import { useSubscription } from '../../hooks/useSubscription'
import { usePlans } from '../../hooks/usePlans'
import { useAuth } from '../../contexts/AuthContext'
import { apiService, ApiError } from '../../services/api'
import { BillingPeriod, Plan } from '../../types'

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

const formatDate = (value?: string | null) => {
  if (!value) return '—'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value))
}

const statusVariant = (status: string): 'success' | 'warning' | 'danger' | 'gray' => {
  if (status === 'active' || status === 'paid' || status === 'succeeded') return 'success'
  if (status === 'trialing' || status === 'open') return 'warning'
  if (status === 'past_due' || status === 'failed' || status === 'unpaid') return 'danger'
  return 'gray'
}

export const BillingPage: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const { subscription, invoices, isLoading, error, refetch } = useSubscription()
  const { plans, isLoading: plansLoading } = usePlans()
  const [billingCycle, setBillingCycle] = useState<BillingPeriod>('monthly')
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)

  const isOwner = user?.dealershipRole === 'owner'
  const subscriptionQuery = searchParams.get('subscription')

  useEffect(() => {
    if (subscriptionQuery === 'success') {
      setToast({ message: t('billing.checkoutSuccess'), type: 'success' })
      void refetch()
      searchParams.delete('subscription')
      setSearchParams(searchParams, { replace: true })
    }
    if (subscriptionQuery === 'cancelled') {
      setToast({ message: t('billing.checkoutCancelled'), type: 'warning' })
      searchParams.delete('subscription')
      setSearchParams(searchParams, { replace: true })
    }
  }, [subscriptionQuery, refetch, searchParams, setSearchParams, t])

  useEffect(() => {
    if (subscription?.billingPeriod) {
      setBillingCycle(subscription.billingPeriod)
    }
  }, [subscription?.billingPeriod])

  const activePlans = useMemo(
    () => plans.filter((plan) => Number(plan.priceMonthly) > 0),
    [plans],
  )

  const currentPlan = useMemo(
    () => plans.find((p) => p.id === subscription?.planId) ?? null,
    [plans, subscription?.planId],
  )

  const yearlyDiscount = useMemo(() => {
    if (!selectedPlan || Number(selectedPlan.priceMonthly) <= 0) return 0
    const monthlyTotal = Number(selectedPlan.priceMonthly) * 12
    const yearly = Number(selectedPlan.priceYearly)
    if (monthlyTotal <= 0) return 0
    return Math.round(((monthlyTotal - yearly) / monthlyTotal) * 100)
  }, [selectedPlan])

  const needsCheckout = subscription?.status === 'incomplete'

  const handleOpenPortal = async () => {
    if (!isOwner) {
      setToast({ message: t('billing.errors.ownerOnlyPortal'), type: 'warning' })
      return
    }
    setActionLoading(true)
    try {
      const { url } = await apiService.createSubscriptionBillingPortal()
      window.location.href = url
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('billing.errors.portal')
      setToast({ message, type: 'error' })
    } finally {
      setActionLoading(false)
    }
  }

  const handlePlanChange = async (plan: Plan) => {
    if (!subscription) {
      setToast({ message: t('billing.errors.noSubscription'), type: 'error' })
      return
    }

    if (plan.id === subscription.planId && subscription.billingPeriod === billingCycle) {
      setToast({ message: t('billing.errors.samePlan'), type: 'info' })
      return
    }

    setActionLoading(true)
    try {
      if (needsCheckout) {
        const { url } = await apiService.createSubscriptionCheckout(plan.id, billingCycle)
        window.location.href = url
        return
      }

      await apiService.changeSubscriptionPlan(plan.id, billingCycle)
      setToast({ message: t('billing.planChanged'), type: 'success' })
      await refetch()
    } catch (err) {
      let message = err instanceof ApiError ? err.message : t('billing.errors.changePlan')
      if (err instanceof ApiError && err.message.includes('downgrade')) {
        message = t('billing.errors.downgrade')
      }
      setToast({ message, type: 'error' })
    } finally {
      setActionLoading(false)
    }
  }

  const handleCancel = async () => {
    if (!window.confirm(t('billing.cancelConfirm'))) return
    setActionLoading(true)
    try {
      await apiService.cancelSubscription()
      setToast({ message: t('billing.cancelScheduled'), type: 'success' })
      await refetch()
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('billing.errors.cancel')
      setToast({ message, type: 'error' })
    } finally {
      setActionLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="h-10 w-10 animate-spin text-accent" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('billing.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('billing.subtitle')}</p>
      </div>

      {error && (
        <AlertMessage variant="error" title={t('billing.errors.load')} message={error} />
      )}

      {!subscription && (
        <AlertMessage
          variant="warning"
          title={t('billing.noSubscriptionTitle')}
          message={t('billing.noSubscriptionDescription')}
        />
      )}

      {subscription && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-accent" />
              {t('billing.currentPlan')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-2xl font-bold capitalize">{subscription.planName}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Badge variant={statusVariant(subscription.status)}>
                    {t(`billing.status.${subscription.status}`, subscription.status)}
                  </Badge>
                  <Badge variant="gray">
                    {subscription.billingPeriod === 'yearly'
                      ? t('billing.yearly')
                      : t('billing.monthly')}
                  </Badge>
                  {subscription.cancelAtPeriodEnd && (
                    <Badge variant="warning">{t('billing.cancelAtPeriodEnd')}</Badge>
                  )}
                </div>
              </div>
              {currentPlan && (
                <p className="text-xl font-semibold text-foreground">
                  {formatCurrency(
                    subscription.billingPeriod === 'yearly'
                      ? Number(currentPlan.priceYearly)
                      : Number(currentPlan.priceMonthly),
                  )}
                  <span className="text-sm font-normal text-muted-foreground ml-1">
                    /{subscription.billingPeriod === 'yearly' ? t('billing.year') : t('billing.month')}
                  </span>
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-surface">
                <Calendar className="h-5 w-5 text-accent mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('billing.nextPayment')}</p>
                  <p className="font-medium">{formatDate(subscription.currentPeriodEnd)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-surface">
                <Receipt className="h-5 w-5 text-accent mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('billing.lastPayment')}</p>
                  <p className="font-medium">{formatDate(subscription.lastPaymentDate)}</p>
                </div>
              </div>
              {subscription.trialEnd && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-surface">
                  <AlertCircle className="h-5 w-5 text-accent mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('billing.trialEnd')}</p>
                    <p className="font-medium">{formatDate(subscription.trialEnd)}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => void handleOpenPortal()}
                disabled={actionLoading || needsCheckout || !isOwner}
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {t('billing.managePayment')}
              </Button>
              {needsCheckout && selectedPlan && (
                <Button
                  variant="default"
                  onClick={() => void handlePlanChange(selectedPlan)}
                  disabled={actionLoading}
                >
                  {t('billing.completePayment')}
                </Button>
              )}
              {isOwner && subscription.status === 'active' && !subscription.cancelAtPeriodEnd && (
                <Button variant="outline" onClick={() => void handleCancel()} disabled={actionLoading}>
                  {t('billing.cancelSubscription')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">{t('billing.plansTitle')}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t('billing.plansSubtitle')}</p>
        </div>

        <BillingToggle
          value={billingCycle}
          onChange={setBillingCycle}
          discount={yearlyDiscount}
        />

        {plansLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-accent" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activePlans.map((plan) => {
              const price =
                billingCycle === 'yearly'
                  ? Number(plan.priceYearly)
                  : Number(plan.priceMonthly)
              const isCurrent = plan.id === subscription?.planId
              return (
                <div key={plan.id} className="space-y-3">
                  <PlanCard
                    plan={plan}
                    isSelected={selectedPlan?.id === plan.id || isCurrent}
                    price={price}
                    billingCycle={billingCycle}
                    discount={billingCycle === 'yearly' ? yearlyDiscount : undefined}
                    onSelect={setSelectedPlan}
                  />
                  <Button
                    className="w-full"
                    variant={isCurrent ? 'outline' : 'default'}
                    disabled={actionLoading || (isCurrent && !needsCheckout)}
                    onClick={() => void handlePlanChange(plan)}
                  >
                    {isCurrent && !needsCheckout
                      ? t('billing.currentPlanBadge')
                      : t('billing.upgradePlan')}
                  </Button>
                </div>
              )
            })}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">{t('billing.invoicesTitle')}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t('billing.invoicesSubtitle')}</p>
        </div>

        <Card>
          <CardContent className="p-0">
            {invoices.length === 0 ? (
              <p className="p-6 text-sm text-muted-foreground">{t('billing.noInvoices')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="p-4 font-medium">{t('billing.invoiceDate')}</th>
                      <th className="p-4 font-medium">{t('billing.invoiceAmount')}</th>
                      <th className="p-4 font-medium">{t('billing.invoiceStatus')}</th>
                      <th className="p-4 font-medium">{t('billing.invoicePaidAt')}</th>
                      <th className="p-4 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-border last:border-0">
                        <td className="p-4">{formatDate(invoice.invoiceDate)}</td>
                        <td className="p-4 font-medium">
                          {formatCurrency(invoice.amountPaid || invoice.amountDue)}
                        </td>
                        <td className="p-4">
                          <Badge variant={statusVariant(invoice.status)}>{invoice.status}</Badge>
                        </td>
                        <td className="p-4">{formatDate(invoice.paidAt)}</td>
                        <td className="p-4 text-right">
                          {invoice.hostedInvoiceUrl && (
                            <a
                              href={invoice.hostedInvoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-accent hover:underline"
                            >
                              {t('billing.viewInvoice')}
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  )
}
